import { useCallback } from 'react';

const STORAGE_KEY = 'peekapak.completionReminder';

const log = (...args: unknown[]) => {
  console.debug('[CompletionReminder:Storage]', ...args);
};

interface DismissalRecord {
  timestamp: number;
  lessonId: string;
}

interface DailyCount {
  date: string;
  count: number;
}

interface CompletionReminderData {
  dismissals: DismissalRecord[];
  lessonsShown: Record<string, number>;
  dailyCount: DailyCount;
  lastShown: number | null;
}

const getDefaultData = (): CompletionReminderData => ({
  dismissals: [],
  lessonsShown: {},
  dailyCount: {
    date: getTodayString(),
    count: 0,
  },
  lastShown: null,
});

const getTodayString = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const getStorageData = (): CompletionReminderData => {
  try {
    // eslint-disable-next-line no-undef
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return getDefaultData();

    const parsed = JSON.parse(stored) as CompletionReminderData;
    return parsed;
  } catch (error) {
    console.error('Error reading completion reminder data from localStorage:', error);
    return getDefaultData();
  }
};

const setStorageData = (data: CompletionReminderData): void => {
  try {
    // eslint-disable-next-line no-undef
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving completion reminder data to localStorage:', error);
  }
};

const cleanupOldData = (data: CompletionReminderData): CompletionReminderData => {
  const now = Date.now();
  const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000;

  // Remove dismissals older than 24 hours
  const recentDismissals = data.dismissals.filter((d) => d.timestamp > twentyFourHoursAgo);

  // Reset daily count if it's a new day
  const today = getTodayString();
  const dailyCount = data.dailyCount.date === today ? data.dailyCount : { date: today, count: 0 };

  return {
    ...data,
    dismissals: recentDismissals,
    dailyCount,
  };
};

export const useCompletionReminderStorage = () => {
  const canShowReminder = useCallback((lessonId: string, completionStatus: string): boolean => {
    log('Checking if can show reminder for lesson:', lessonId);

    // Don't show if lesson already completed
    if (completionStatus === 'Completed') {
      log('❌ Cannot show: Lesson already completed');
      return false;
    }

    let data = getStorageData();
    data = cleanupOldData(data);

    // Check: Already shown for this lesson 2+ times
    const lessonShowCount = data.lessonsShown[lessonId] || 0;
    log(`Lesson show count for ${lessonId}:`, lessonShowCount);
    if (lessonShowCount >= 2) {
      log('❌ Cannot show: Already shown for this lesson 2+ times');
      return false;
    }

    // Check: Dismissed 2+ times in last 24 hours (across all lessons)
    log('Recent dismissals (last 24h):', data.dismissals.length);
    if (data.dismissals.length >= 2) {
      log('❌ Cannot show: Dismissed 2+ times in last 24 hours');
      return false;
    }

    // Check: Already shown within last 15 minutes
    if (data.lastShown) {
      const minutesSinceLastShown = Math.floor((Date.now() - data.lastShown) / 60000);
      log(`Minutes since last shown: ${minutesSinceLastShown}`);
      if (Date.now() - data.lastShown < 15 * 60 * 1000) {
        log('❌ Cannot show: Shown within last 15 minutes');
        return false;
      }
    }

    // Check: Already shown 3+ times today (across all lessons)
    const today = getTodayString();
    log(`Daily count for ${today}:`, data.dailyCount.date === today ? data.dailyCount.count : 0);
    if (data.dailyCount.date === today && data.dailyCount.count >= 3) {
      log('❌ Cannot show: Already shown 3+ times today');
      return false;
    }

    // Save cleaned data back to storage
    setStorageData(data);

    log('✅ Can show reminder!');
    return true;
  }, []);

  const recordDismissal = useCallback((lessonId: string): void => {
    log('Recording dismissal for lesson:', lessonId);
    let data = getStorageData();
    data = cleanupOldData(data);

    // Add dismissal record
    data.dismissals.push({
      timestamp: Date.now(),
      lessonId,
    });

    log('Total dismissals in last 24h:', data.dismissals.length);
    setStorageData(data);
  }, []);

  const recordShown = useCallback((lessonId: string): void => {
    log('Recording shown for lesson:', lessonId);
    let data = getStorageData();
    data = cleanupOldData(data);

    // Increment lesson-specific count
    const newLessonCount = (data.lessonsShown[lessonId] || 0) + 1;
    data.lessonsShown[lessonId] = newLessonCount;
    log(`Lesson show count updated to: ${newLessonCount}`);

    // Update last shown timestamp
    data.lastShown = Date.now();

    // Increment daily count
    const today = getTodayString();
    if (data.dailyCount.date === today) {
      data.dailyCount.count += 1;
    } else {
      data.dailyCount = { date: today, count: 1 };
    }
    log(`Daily count for ${today}: ${data.dailyCount.count}`);

    setStorageData(data);
  }, []);

  const rollbackShown = useCallback((lessonId: string): void => {
    log('Rolling back shown count for lesson (network interruption):', lessonId);
    const data = getStorageData();

    // Decrement lesson-specific count
    if (data.lessonsShown[lessonId] && data.lessonsShown[lessonId] > 0) {
      data.lessonsShown[lessonId] -= 1;
      log(`Lesson show count rolled back to: ${data.lessonsShown[lessonId]}`);

      // Remove the key if count is 0
      if (data.lessonsShown[lessonId] === 0) {
        delete data.lessonsShown[lessonId];
      }
    }

    // Also decrement daily count if it was today
    const today = getTodayString();
    if (data.dailyCount.date === today && data.dailyCount.count > 0) {
      data.dailyCount.count -= 1;
      log(`Daily count rolled back to: ${data.dailyCount.count}`);
    }

    // Don't roll back lastShown timestamp - keep it to maintain 15-minute cooldown
    // This prevents rapid re-showing if network flickers on/off repeatedly
    log('Note: Keeping lastShown timestamp to maintain 15-minute cooldown');

    setStorageData(data);
  }, []);

  return {
    canShowReminder,
    recordDismissal,
    recordShown,
    rollbackShown,
  };
};
